/**
 * Boclips Integration Service
 * Handles Boclips client credentials integration and navigation to Boclips content page
 */

/**
 * Prepares Boclips token and navigates to Boclips content page
 * Note: Boclips uses client credentials flow, not browser-based SSO redirect
 * @param {string} userId - The user ID to prepare token for
 * @param {Object} options - Optional configuration
 * @param {Function} [options.navigate] - Navigation function (from react-router-dom)
 * @param {boolean} [options.openInNewTab] - If true, opens Boclips page in a new browser tab
 * @returns {Promise<Object>} Result object with success status and details
 */
export async function launchBoclipsSso(userId, options = {}) {
  try {
    const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
    if (!adminApiKey) {
      throw new Error('REACT_APP_ADMIN_API_KEY not configured');
    }

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
    const params = new URLSearchParams({
      userId: userId,
    });
    const fullUrl = `${apiBaseUrl}/providers/boclips/launch?${params.toString()}`;

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'x-api-key': adminApiKey,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || errorData.message || 'Failed to prepare Boclips token');
    }

    const data = await response.json();

    if (data.success && data.hasToken) {
      const relativePath = '/providers/boclips';

      if (options.openInNewTab) {
        const userData = sessionStorage.getItem('currentUser');
        if (userData) {
          localStorage.setItem('boclips_currentUser', userData);
        }
        const fullUrl = `${window.location.origin}${relativePath}`;
        window.open(fullUrl, '_blank');
      } else if (options.navigate && typeof options.navigate === 'function') {
        options.navigate(relativePath);
      } else {
        window.location.href = relativePath;
      }
    } else {
      throw new Error('Token preparation failed or invalid response');
    }

    return {
      success: true,
      message: 'Boclips token ready. Navigating to content page.',
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to prepare Boclips token',
      details: error,
    };
  }
}

/**
 * Checks if the provider is Boclips
 * @param {Object|string} providerOrKey 
 * @returns {boolean}
 */
export const isBoclipsProvider = (providerOrKey) => {
  const key = typeof providerOrKey === 'string' ? providerOrKey : providerOrKey?.key;
  return key === 'boclips';
};

/**
 * Gets Boclips specific error message
 * @param {Object} error 
 * @returns {string}
 */
export const getBoclipsErrorMessage = (error) => {
  return 'Failed to launch Boclips. Please try again or contact support.';
};
